/**
 * Hero - minaCorale JavaScript (HS-HERO-MC-001)
 * ==============================================
 * 3パターンのHeroセクション用インタラクション
 *
 * 機能:
 * - パターン別初期化
 * - 段階アニメーション（パターン3）
 * - スクロールインジケーター
 * - Shopify テーマエディタ対応
 * - prefers-reduced-motion 対応
 */

class HeroMinaCorale {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.pattern = section.dataset.pattern;
    this.colorScheme = section.dataset.colorScheme;
    this.stagedAnimation = section.dataset.stagedAnimation === 'true';
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // スライドショー設定
    this.enableMosaic = section.dataset.enableMosaic === 'true';
    this.enableSlideshow = section.dataset.enableSlideshow === 'true';
    this.slideshowInterval = parseInt(section.dataset.slideshowInterval, 10) * 1000 || 5000;
    this.slideshowTransition = section.dataset.slideshowTransition || 'crossfade';

    // スライドショータイマー
    this.slideshowTimer = null;
    this.isPaused = false;

    this.init();
  }

  init() {
    // prefers-reduced-motion の場合は即座に全要素を表示
    if (this.prefersReducedMotion) {
      this.showAllElements();
      this.pauseVideo();
      return;
    }

    // パターン別初期化
    switch (this.pattern) {
      case 'fullscreen':
        this.initFullscreen();
        break;
      case 'split':
        this.initSplit();
        break;
      case 'staged':
        this.initStaged();
        break;
    }

    // 共通: IntersectionObserver でフェードイン
    this.initIntersectionObserver();
  }

  /**
   * モーション低減時: 全要素を即座に表示
   */
  showAllElements() {
    const animatedElements = this.section.querySelectorAll(
      '.hero-mc__fade-in, .hero-mc__slide-left, .hero-mc__slide-right, .hero-mc__tagline-part, .hero-mc__product-icons, .hero-mc__staged-item'
    );
    animatedElements.forEach(el => el.classList.add('is-visible'));
  }

  /**
   * モーション低減時: 動画を一時停止
   */
  pauseVideo() {
    const video = this.section.querySelector('.hero-mc__background-video');
    if (video) {
      video.pause();
    }
  }

  /**
   * パターン1: フルスクリーン
   * - スクロールインジケーター制御
   * - 初期フェードインアニメーション
   */
  initFullscreen() {
    // スクロールインジケーターの表示/非表示
    const scrollIndicator = this.section.querySelector('.hero-mc__scroll-indicator');
    if (scrollIndicator) {
      window.addEventListener('scroll', () => {
        if (window.scrollY > 100) {
          scrollIndicator.style.opacity = '0';
        } else {
          scrollIndicator.style.opacity = '0.7';
        }
      }, { passive: true });
    }

    // コンテンツフェードイン
    this.animateFullscreenContent();
  }

  /**
   * フルスクリーンコンテンツのフェードイン
   */
  animateFullscreenContent() {
    const content = this.section.querySelector('.hero-mc__content');
    if (!content) return;

    const elements = content.querySelectorAll('.hero-mc__fade-in');
    elements.forEach((el, index) => {
      setTimeout(() => {
        el.classList.add('is-visible');
      }, 300 + (index * 100));
    });
  }

  /**
   * パターン2: スプリット
   * - 左右からのスライドインアニメーション
   * - 商品カードホバーエフェクト
   * - 段階演出モード対応
   * - モザイクモード対応
   * - スライドショー対応
   */
  initSplit() {
    // モザイクモードのチェック
    const mosaicContainer = this.section.querySelector('.hero-mc__mosaic');
    if (mosaicContainer) {
      this.initSplitMosaic(mosaicContainer);
    }

    // スライドショー初期化
    if (this.enableSlideshow) {
      this.initSlideshow();
    }

    // 段階演出が有効な場合
    if (this.stagedAnimation) {
      this.animateSplitStaged();
      return;
    }

    // 左コンテンツ: 左からスライドイン
    const leftContent = this.section.querySelector('.hero-mc__left-content');
    if (leftContent) {
      setTimeout(() => {
        leftContent.classList.add('is-visible');
      }, 200);
    }

    // 右商品カード: 右からスライドイン（stagger）- 従来モードのみ
    const productCards = this.section.querySelectorAll('.hero-mc__product-card');
    productCards.forEach((card, index) => {
      setTimeout(() => {
        card.classList.add('is-visible');
      }, 400 + (index * 150));
    });
  }

  /**
   * パターン2: モザイクモード
   * harmonia-mosaic.js の機能を活用
   */
  initSplitMosaic(container) {
    // harmonia-mosaic.js の HarmoniaMosaic クラスを使用
    if (window.HarmoniaMosaic) {
      // 既存インスタンスがあれば破棄
      if (container._harmoniaMosaic) {
        container._harmoniaMosaic.destroy();
      }

      // 新規インスタンス作成
      container.dataset.initialized = 'false';
      this.mosaic = new window.HarmoniaMosaic(container);
      container.dataset.initialized = 'true';
      container._harmoniaMosaic = this.mosaic;

      console.log('[HeroMinaCorale] Mosaic mode initialized');
    } else {
      console.warn('[HeroMinaCorale] HarmoniaMosaic not found. Make sure harmonia-mosaic.js is loaded.');
    }
  }

  /* ===========================================
     スライドショー機能
     =========================================== */

  /**
   * スライドショー初期化
   * モザイクON: 商品セット切り替え
   * モザイクOFF: 背景画像切り替え
   */
  initSlideshow() {
    if (this.prefersReducedMotion) {
      console.log('[HeroMinaCorale] Slideshow disabled (prefers-reduced-motion)');
      return;
    }

    // トランジションクラスを追加
    if (this.slideshowTransition !== 'crossfade') {
      this.section.classList.add(`hero-mc--slideshow-${this.slideshowTransition}`);
    }

    if (this.enableMosaic) {
      this.initMosaicSlideshow();
    } else {
      this.initBackgroundSlideshow();
    }

    console.log('[HeroMinaCorale] Slideshow initialized:', {
      mode: this.enableMosaic ? 'mosaic' : 'background',
      interval: this.slideshowInterval,
      transition: this.slideshowTransition
    });
  }

  /**
   * 商品セット切り替えスライドショー（モザイクモード）
   * data-set-group 属性でグループ分けされた商品をセット単位で切り替え
   * シズル感のあるポップインアニメーションで表示
   */
  initMosaicSlideshow() {
    const container = this.section.querySelector('.hero-mc__mosaic');
    if (!container) return;

    const items = container.querySelectorAll('.harmonia-mosaic__item');
    if (items.length === 0) {
      console.log('[HeroMinaCorale] No products found for mosaic slideshow');
      return;
    }

    // data-set-group 属性でグループ化
    const groups = {};
    items.forEach(item => {
      const groupId = item.dataset.setGroup || '1';
      if (!groups[groupId]) groups[groupId] = [];
      groups[groupId].push(item);
    });

    const groupIds = Object.keys(groups).sort((a, b) => parseInt(a) - parseInt(b));

    // セットが1つだけなら切り替え不要
    if (groupIds.length < 2) {
      console.log('[HeroMinaCorale] Only one product set, slideshow disabled');
      return;
    }

    console.log('[HeroMinaCorale] Product sets found:', groupIds.map(gid => `Set ${gid}: ${groups[gid].length} items`));

    let currentSetIndex = 0;
    this.mosaicGroups = groups;
    this.mosaicGroupIds = groupIds;

    // 初期表示: セット1以外を非表示
    groupIds.forEach((gid, idx) => {
      groups[gid].forEach(item => {
        if (idx !== 0) item.classList.add('is-hidden');
      });
    });

    // 自動切り替え
    this.slideshowTimer = setInterval(() => {
      if (this.isPaused) return;

      const fromGroupId = groupIds[currentSetIndex];
      currentSetIndex = (currentSetIndex + 1) % groupIds.length;
      const toGroupId = groupIds[currentSetIndex];

      // フェードアウト（現在のセット）
      groups[fromGroupId].forEach(item => item.classList.add('is-hidden'));

      // フェードイン（次のセット）- スタッガーアニメーション
      setTimeout(() => {
        groups[toGroupId].forEach((item, idx) => {
          item.classList.remove('is-hidden');
          item.classList.add('is-entering');
          // アニメーション終了後にクラスを削除
          setTimeout(() => {
            item.classList.remove('is-entering');
          }, 600 + idx * 80);
        });
      }, 400);
    }, this.slideshowInterval);

    // ホバー時一時停止
    container.addEventListener('mouseenter', () => this.pauseSlideshow());
    container.addEventListener('mouseleave', () => this.resumeSlideshow());
  }

  /**
   * 背景画像スライドショー（モザイクOFF時）
   * Ken Burns + クロスフェード
   */
  initBackgroundSlideshow() {
    const slidesContainer = this.section.querySelector('.hero-mc__slides');
    if (!slidesContainer) {
      console.log('[HeroMinaCorale] No slides container found');
      return;
    }

    const slides = slidesContainer.querySelectorAll('.hero-mc__slide');
    if (slides.length < 2) {
      console.log('[HeroMinaCorale] Not enough slides for slideshow');
      return;
    }

    let currentSlide = 0;

    // 最初のスライドをアクティブに
    slides[0].classList.add('is-active');

    // ドットインジケーターを作成
    this.createSlideshowDots(slides.length, slidesContainer.parentElement);

    // 自動切り替え
    this.slideshowTimer = setInterval(() => {
      if (this.isPaused) return;

      const prevSlide = currentSlide;
      currentSlide = (currentSlide + 1) % slides.length;

      // ★ Ken Burns継続: フェードアウト中クラスを追加（アニメーションを維持）
      slides[prevSlide].classList.add('is-fading-out');

      // トランジション
      slides[prevSlide].classList.remove('is-active');
      if (this.slideshowTransition === 'slide') {
        slides[prevSlide].classList.add('is-exiting');
        setTimeout(() => slides[prevSlide].classList.remove('is-exiting'), 800);
      }
      slides[currentSlide].classList.add('is-active');

      // ドット更新
      this.updateSlideshowDots(currentSlide);

      // ★ フェード完了後、旧スライドをクリーンアップ（トランジション時間1s後）
      setTimeout(() => {
        slides[prevSlide].classList.remove('is-fading-out');
      }, 1000);
    }, this.slideshowInterval);

    // ホバー時一時停止
    slidesContainer.addEventListener('mouseenter', () => this.pauseSlideshow());
    slidesContainer.addEventListener('mouseleave', () => this.resumeSlideshow());
  }

  /**
   * スライドショードットインジケーターを作成
   */
  createSlideshowDots(count, parent) {
    const dotsContainer = document.createElement('div');
    dotsContainer.className = 'hero-mc__slideshow-dots';

    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.className = 'hero-mc__slideshow-dot';
      if (i === 0) dot.classList.add('is-active');
      dot.setAttribute('aria-label', `スライド ${i + 1}`);
      dot.dataset.index = i;

      dot.addEventListener('click', () => {
        this.goToSlide(i);
      });

      dotsContainer.appendChild(dot);
    }

    parent.appendChild(dotsContainer);
    this.dotsContainer = dotsContainer;
  }

  /**
   * ドットインジケーターを更新
   */
  updateSlideshowDots(activeIndex) {
    if (!this.dotsContainer) return;

    const dots = this.dotsContainer.querySelectorAll('.hero-mc__slideshow-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  }

  /**
   * 特定のスライドに移動
   */
  goToSlide(index) {
    const slidesContainer = this.section.querySelector('.hero-mc__slides');
    if (!slidesContainer) return;

    const slides = slidesContainer.querySelectorAll('.hero-mc__slide');

    // 現在アクティブなスライドを非アクティブに
    slides.forEach(slide => slide.classList.remove('is-active'));

    // 指定スライドをアクティブに
    slides[index].classList.add('is-active');

    // ドット更新
    this.updateSlideshowDots(index);

    // タイマーリセット
    this.resetSlideshowTimer();
  }

  /**
   * スライドショーを一時停止
   */
  pauseSlideshow() {
    this.isPaused = true;
    this.section.classList.add('hero-mc--slideshow-paused');
  }

  /**
   * スライドショーを再開
   */
  resumeSlideshow() {
    this.isPaused = false;
    this.section.classList.remove('hero-mc--slideshow-paused');
  }

  /**
   * スライドショータイマーをリセット
   */
  resetSlideshowTimer() {
    if (this.slideshowTimer) {
      clearInterval(this.slideshowTimer);
    }

    // モードに応じて再初期化
    if (this.enableMosaic) {
      this.initMosaicSlideshow();
    } else {
      this.initBackgroundSlideshow();
    }
  }

  /**
   * スライドショーを停止
   */
  stopSlideshow() {
    if (this.slideshowTimer) {
      clearInterval(this.slideshowTimer);
      this.slideshowTimer = null;
    }
    if (this.dotsContainer) {
      this.dotsContainer.remove();
      this.dotsContainer = null;
    }
  }

  /**
   * パターン2: 段階演出モード
   * テキストを順番に表示するアニメーション
   */
  animateSplitStaged() {
    const stagedItems = this.section.querySelectorAll('.hero-mc__staged-item');
    // 速度設定をdata属性から取得（デフォルト: 600ms）
    const speed = parseInt(this.section.dataset.stagedSpeed, 10) || 600;

    // 各要素を段階的に表示
    // 0ms: ロゴ/ブランド名
    // speed ms: タグライン
    // speed*2 ms: サブコピー
    // speed*3 ms: 説明文
    // speed*4 ms: CTA
    stagedItems.forEach((item, index) => {
      const delay = index * speed;
      setTimeout(() => {
        item.classList.add('is-visible');
      }, delay);
    });

    // 右商品カード: 段階演出完了後に表示
    const productCards = this.section.querySelectorAll('.hero-mc__product-card');
    const baseDelay = stagedItems.length * speed;
    productCards.forEach((card, index) => {
      setTimeout(() => {
        card.classList.add('is-visible');
      }, baseDelay + (index * 150));
    });
  }

  /**
   * パターン3: 段階演出
   * - タグラインの段階的表示
   * - 説明文・CTAの遅延表示
   * - 商品アイコンの最後に表示
   */
  initStaged() {
    // 速度設定をdata属性から取得（デフォルト: 600ms）
    const speed = parseInt(this.section.dataset.stagedSpeed, 10) || 600;
    const parts = this.section.querySelectorAll('.hero-mc__tagline-part');

    // タグラインパーツの段階表示
    this.animateTaglineParts();

    // 説明文・CTAの表示（タグライン完了後）
    // 計算: ロゴ開始(speed) + パーツ数 * speed + 余裕
    const descriptionDelay = speed + (parts.length * speed) + speed;
    setTimeout(() => {
      const description = this.section.querySelector('.hero-mc__description');
      const cta = this.section.querySelector('.hero-mc__cta-wrapper');
      if (description) description.classList.add('is-visible');
      if (cta) cta.classList.add('is-visible');
    }, descriptionDelay);

    // 商品アイコンの表示（説明文の後）
    setTimeout(() => {
      const productIcons = this.section.querySelector('.hero-mc__product-icons');
      if (productIcons) productIcons.classList.add('is-visible');
    }, descriptionDelay + speed);
  }

  /**
   * タグラインパーツのアニメーション
   * 「おいしく、」「たのしく、」「まあるい時間。」を順番に表示
   */
  animateTaglineParts() {
    // 速度設定をdata属性から取得（デフォルト: 600ms）
    const speed = parseInt(this.section.dataset.stagedSpeed, 10) || 600;
    const logo = this.section.querySelector('.hero-mc__logo');
    const parts = this.section.querySelectorAll('.hero-mc__tagline-part');

    // ロゴ表示
    if (logo) {
      setTimeout(() => {
        logo.classList.add('is-visible');
      }, 0);
    }

    // タグラインパーツ表示
    // 最初のパーツは speed ms後、以降は speed ms 間隔
    parts.forEach((part, index) => {
      const delay = speed + (index * speed);
      setTimeout(() => {
        part.classList.add('is-visible');
      }, delay);
    });
  }

  /**
   * IntersectionObserver でスクロール時のアニメーション
   */
  initIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, options);

    // 監視対象要素のセレクタを構築
    // 段階演出モードの場合は .hero-mc__staged-item を除外（animateSplitStaged() で処理するため）
    const selectors = [
      '.hero-mc__fade-in:not(.is-visible)',
      '.hero-mc__slide-left:not(.is-visible)',
      '.hero-mc__slide-right:not(.is-visible)'
    ];
    if (!this.stagedAnimation) {
      selectors.push('.hero-mc__staged-item:not(.is-visible)');
    }
    const targets = this.section.querySelectorAll(selectors.join(', '));
    targets.forEach(target => observer.observe(target));
  }

  /**
   * セクションの再初期化（テーマエディタ用）
   */
  reinit() {
    // スライドショーを停止
    this.stopSlideshow();

    // 全アニメーションクラスをリセット
    const animatedElements = this.section.querySelectorAll(
      '.is-visible'
    );
    animatedElements.forEach(el => el.classList.remove('is-visible'));

    // トランジションクラスをリセット
    this.section.classList.remove(
      'hero-mc--slideshow-slide',
      'hero-mc--slideshow-zoom',
      'hero-mc--slideshow-paused'
    );

    // 設定を再読み込み
    this.enableMosaic = this.section.dataset.enableMosaic === 'true';
    this.enableSlideshow = this.section.dataset.enableSlideshow === 'true';
    this.slideshowInterval = parseInt(this.section.dataset.slideshowInterval, 10) * 1000 || 5000;
    this.slideshowTransition = this.section.dataset.slideshowTransition || 'crossfade';

    // 再初期化
    this.init();
  }

  /**
   * クリーンアップ
   */
  destroy() {
    // スライドショーを停止
    this.stopSlideshow();

    // イベントリスナーのクリーンアップが必要な場合はここに追加
  }
}

/**
 * 初期化
 */
function initHeroMinaCorale() {
  const sections = document.querySelectorAll('.hero-mc');
  sections.forEach(section => {
    if (!section.heroMinaCorale) {
      section.heroMinaCorale = new HeroMinaCorale(section);
    }
  });
}

// DOMContentLoaded で初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroMinaCorale);
} else {
  initHeroMinaCorale();
}

/**
 * Shopify テーマエディタ対応
 */
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.hero-mc');
  if (section) {
    // 既存インスタンスを破棄
    if (section.heroMinaCorale) {
      section.heroMinaCorale.destroy();
    }
    // 新規インスタンス作成
    section.heroMinaCorale = new HeroMinaCorale(section);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  const section = event.target.querySelector('.hero-mc');
  if (section && section.heroMinaCorale) {
    section.heroMinaCorale.destroy();
    delete section.heroMinaCorale;
  }
});

// 設定変更時の再初期化
document.addEventListener('shopify:section:select', (event) => {
  const section = event.target.querySelector('.hero-mc');
  if (section && section.heroMinaCorale) {
    section.heroMinaCorale.reinit();
  }
});
